import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Check, X, Users, Clock, CheckCircle, AlertCircle, Edit2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffAttendance = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editMode, setEditMode] = useState({});

  useEffect(() => {
    fetchAttendanceForDate(selectedDate);
  }, [selectedDate]);

  const fetchAttendanceForDate = async (date) => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/staff-attendance/date/${date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttendanceData(data);
        
        // Initialize statuses - keep actual status, don't default unmarked to Present
        const initialStatuses = {};
        data.staff_attendance.forEach(staff => {
          initialStatuses[staff.staff_id] = staff.status || null;  // Keep null if not marked
        });
        setEditMode(initialStatuses);
      } else {
        throw new Error('Failed to fetch attendance');
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setMessage({ type: 'error', text: 'Failed to load attendance data' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (staffId, status) => {
    setEditMode(prev => ({ ...prev, [staffId]: status }));
  };

  const handleMarkAll = (status) => {
    const newStatuses = {};
    attendanceData.staff_attendance.forEach(staff => {
      newStatuses[staff.staff_id] = status;
    });
    setEditMode(newStatuses);
  };

  const handleSaveAttendance = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare attendance records - only include staff with selected status
      const attendance_records = attendanceData.staff_attendance
        .filter(staff => editMode[staff.staff_id])  // Only include if status is selected
        .map(staff => ({
          staff_id: staff.staff_id,
          status: editMode[staff.staff_id],
          check_in_time: staff.check_in_time || null,
          check_out_time: staff.check_out_time || null,
          remarks: ''
        }));
      
      if (attendance_records.length === 0) {
        setMessage({ type: 'error', text: 'Please select attendance status for at least one staff member' });
        setSaving(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/staff-attendance/bulk-mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate,
          attendance_records
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `Attendance saved! ${data.saved} new, ${data.updated} updated` 
        });
        
        // Refresh data
        await fetchAttendanceForDate(selectedDate);
      } else {
        throw new Error(data.detail || 'Failed to save attendance');
      }
    } catch (error) {
      console.error('Error saving attendance:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save attendance' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Present': { color: 'bg-green-100 text-green-800', icon: <Check className="h-3 w-3" /> },
      'Absent': { color: 'bg-red-100 text-red-800', icon: <X className="h-3 w-3" /> },
      'Half Day': { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
      'Leave': { color: 'bg-blue-100 text-blue-800', icon: <Calendar className="h-3 w-3" /> },
      'Paid Leave': { color: 'bg-purple-100 text-purple-800', icon: <Calendar className="h-3 w-3" /> },
      'Late': { color: 'bg-orange-100 text-orange-800', icon: <Clock className="h-3 w-3" /> }
    };
    
    const config = statusConfig[status] || statusConfig['Present'];
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const canEdit = isToday || new Date(selectedDate) <= new Date(); // Can edit today or past dates

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Staff Attendance</h1>
        <p className="text-gray-600 mt-1">Mark and manage daily attendance for all staff members</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      {attendanceData && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Staff</p>
                  <p className="text-2xl font-bold text-gray-900">{attendanceData.total_staff}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700">Not Marked</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {Object.values(editMode).filter(s => !s).length}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Present</p>
                  <p className="text-2xl font-bold text-green-600">
                    {Object.values(editMode).filter(s => s === 'Present').length}
                  </p>
                </div>
                <Check className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Absent</p>
                  <p className="text-2xl font-bold text-red-600">
                    {Object.values(editMode).filter(s => s === 'Absent').length}
                  </p>
                </div>
                <X className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Half Day</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {Object.values(editMode).filter(s => s === 'Half Day').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">On Leave</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {Object.values(editMode).filter(s => s === 'Leave' || s === 'Paid Leave').length}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date Selection and Actions */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attendance Date
              </label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full"
              />
            </div>
            
            {canEdit && (
              <>
                <Button
                  onClick={() => handleMarkAll('Present')}
                  variant="outline"
                  className="gap-2"
                  disabled={saving}
                >
                  <Check className="h-4 w-4" />
                  Mark All Present
                </Button>
                
                <Button
                  onClick={() => handleMarkAll('Absent')}
                  variant="outline"
                  className="gap-2"
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                  Mark All Absent
                </Button>
                
                <Button
                  onClick={handleSaveAttendance}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  disabled={saving}
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </>
            )}
          </div>
          
          {attendanceData?.is_locked && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              This attendance is from a past date. Admin can still edit if needed.
            </div>
          )}
          
          {attendanceData?.marked_count > 0 && attendanceData?.marked_count === attendanceData?.total_staff && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <CheckCircle className="h-4 w-4 inline mr-2" />
              Attendance Done - All staff attendance marked for this date
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Staff Attendance List</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : attendanceData?.staff_attendance?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>No staff members found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Marked By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceData?.staff_attendance?.map((staff, index) => (
                    <tr key={staff.staff_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{staff.employee_id}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{staff.full_name}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{staff.department || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{staff.designation || '-'}</td>
                      <td className="px-6 py-4 text-center">
                        {canEdit ? (
                          <Select
                            value={editMode[staff.staff_id] || ''}
                            onValueChange={(value) => handleStatusChange(staff.staff_id, value)}
                          >
                            <SelectTrigger className={`w-[140px] mx-auto ${!editMode[staff.staff_id] ? 'border-orange-300 bg-orange-50' : ''}`}>
                              <SelectValue placeholder="-- Select --" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Present">✓ Present</SelectItem>
                              <SelectItem value="Absent">✗ Absent</SelectItem>
                              <SelectItem value="Half Day">⌚ Half Day</SelectItem>
                              <SelectItem value="Leave">📅 Leave</SelectItem>
                              <SelectItem value="Paid Leave">📅 Paid Leave</SelectItem>
                              <SelectItem value="Late">⏰ Late</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          staff.status ? getStatusBadge(staff.status) : <span className="text-gray-400">Not marked</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-sm">
                        {staff.status ? (
                          <div>
                            {staff.marked_by_self ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                📱 Self (Online)
                              </span>
                            ) : staff.marked_by ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                👤 {staff.marked_by}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                ✓ Marked
                              </span>
                            )}
                            {staff.check_in_time && (
                              <div className="text-xs text-gray-500 mt-1">
                                In: {new Date(staff.check_in_time).toLocaleTimeString('en-IN', { 
                                  hour: '2-digit', 
                                  minute: '2-digit',
                                  hour12: true,
                                  timeZone: 'Asia/Kolkata'
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Not marked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffAttendance;